<script setup>
import { ref, computed } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { groupExpensesByDate } from '@/composables/expense-claim'

import ExpenseDayHeader from '@/components/accounting/ExpenseDayHeader.vue'
import ExpenseRowListItem from '@/components/accounting/ExpenseRowListItem.vue'

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
})
const selectedIds = defineModel({ type: Array, required: true })

const i18n = getI18n()

const projectFilter = ref(null)
const projectFilterOptions = computed(() => [...new Map(props.rows.map(row => [row.project ?? null, {
  project: row.project ?? null,
  label: row.project_name ?? i18n.t('common.generalExpenses'),
  count: props.rows.filter(other => (other.project ?? null) === (row.project ?? null)).length,
}])).values()])
const projectFilterOption = computed(() => projectFilter.value && projectFilterOptions.value.find(option => option.project === projectFilter.value.project))
const visibleRows = computed(() => (projectFilter.value ? props.rows.filter(row => (row.project ?? null) === projectFilter.value.project) : props.rows))
const visibleDays = computed(() => groupExpensesByDate(visibleRows.value))
const isSelected = row => selectedIds.value.includes(row.id)
const areAllVisibleSelected = computed(() => visibleRows.value.every(isSelected))
const visibleSelectionState = computed(() => {
  if (areAllVisibleSelected.value) return true
  return visibleRows.value.some(isSelected) ? null : false
})

const onProjectFilterChanged = (option) => { projectFilter.value = option }
const toggleVisibleRows = () => {
  const visibleIds = visibleRows.value.map(row => row.id)
  selectedIds.value = areAllVisibleSelected.value
    ? selectedIds.value.filter(id => !visibleIds.includes(id))
    : [...new Set([...selectedIds.value, ...visibleIds])]
}
const toggleRow = (row) => {
  selectedIds.value = selectedIds.value.includes(row.id) ? selectedIds.value.filter(id => id !== row.id) : [...selectedIds.value, row.id]
}
</script>

<template>
  <div class="column q-gutter-y-sm">
    <div class="row no-wrap items-center q-gutter-x-sm">
      <q-checkbox
        :model-value="visibleSelectionState"
        @update:model-value="toggleVisibleRows"
      >
        <q-tooltip>{{ areAllVisibleSelected ? i18n.t('common.selectNone') : i18n.t('common.selectAll') }}</q-tooltip>
      </q-checkbox>
      <q-select
        class="col"
        dense
        :model-value="projectFilterOption"
        :options="projectFilterOptions"
        clearable
        :option-label="option => `${option.label} · ${option.count}`"
        :label="i18n.t('common.project')"
        @update:model-value="onProjectFilterChanged"
      />
    </div>
    <div v-for="day in visibleDays" :key="day.date">
      <expense-day-header :date="day.date" :total="day.total" />
      <q-list>
        <q-item
          v-for="row in day.rows"
          :key="row.id"
          class="q-pa-none"
          :class="{ 'selected-row': isSelected(row) }"
          clickable
          @click="toggleRow(row)"
        >
          <expense-row-list-item
            :row="row"
            :show-project="projectFilter === null"
            :selected="isSelected(row)"
            class="full-width"
          />
        </q-item>
      </q-list>
    </div>
  </div>
</template>
